import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
  Rating,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from '../utils/api';
import ReCAPTCHA from "react-google-recaptcha";

// Добавляем константу для sitekey
const RECAPTCHA_SITE_KEY = '6LdbUQMrAAAAANQ0qoqh-3AXEAmR2_9RyEeJH9IJ';

function Reviews() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token, logout } = useAuth();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [editFormData, setEditFormData] = useState({
    email: "",
    rating: 0,
    text: "",
    isVerified: false,
    isApproved: false,
    createdAt: ""
  });
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [createFormData, setCreateFormData] = useState({
    productId: "",
    email: "",
    rating: 0,
    text: "",
    createdAt: "",
    isVerified: true,
    isApproved: true
  });

  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "info"
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchReviews(),
          fetchProducts(),
          fetchCategories()
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Ошибка при загрузке данных");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("Ошибка при загрузке продуктов");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Ошибка при загрузке категорий");
    }
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await api.get("/reviews");
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setError("Ошибка при загрузке отзывов");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCreateClick = () => {
    // Устанавливаем текущую дату в локальном формате для datetime-local input
    const now = new Date();
    const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
    const formattedDate = localDate.toISOString().slice(0, 16);

    setCreateFormData({
      productId: "",
      email: "",
      rating: 0,
      text: "",
      createdAt: formattedDate,
      isVerified: true,
      isApproved: true
    });
    setSelectedCategory("");
    setCreateDialogOpen(true);
  };

  const handleCreateInputChange = (event) => {
    const { name, value } = event.target;

    if (name === 'categoryId') {
      console.log('Selected category:', value);
      setSelectedCategory(value);
      // Reset product when category changes
      setCreateFormData({
        ...createFormData,
        [name]: value,
        productId: ''
      });
    } else {
      setCreateFormData({
        ...createFormData,
        [name]: value
      });
    }
  };

  const handleRequestCode = async () => {
    try {
      await api.post("/reviews/request-code", {
        email: createFormData.email
      });
      setError(null);
      setAlert({
        show: true,
        message: "Код подтверждения отправлен на вашу почту",
        severity: "success"
      });
    } catch (error) {
      console.error("Error requesting verification code:", error);
      setError("Ошибка при отправке кода подтверждения");
    }
  };

  const handleVerifyCode = async () => {
    try {
      await api.post("/reviews/verify-code", {
        email: createFormData.email,
        verificationCode: createFormData.verificationCode
      });
      setCreateFormData(prev => ({ ...prev, isEmailVerified: true }));
      setError(null);
      setAlert({
        show: true,
        message: "Email подтвержден",
        severity: "success"
      });
    } catch (error) {
      console.error("Error verifying code:", error);
      setError("Неверный код подтверждения");
    }
  };

  const handleCreateSubmit = async () => {
    try {
      if (!createFormData.productId) {
        setError("Пожалуйста, выберите продукт");
        return;
      }

      // Преобразуем строку даты в объект Date в локальной временной зоне
      const [datePart, timePart] = createFormData.createdAt.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      
      // Создаем дату в локальной временной зоне
      const localDate = new Date(year, month - 1, day, hours, minutes);
      
      // Преобразуем в UTC
      const utcDate = new Date(Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        localDate.getHours(),
        localDate.getMinutes()
      ));

      const reviewData = {
        productId: createFormData.productId,
        email: createFormData.email,
        rating: createFormData.rating,
        text: createFormData.text,
        createdAt: utcDate.toISOString(),
        isVerified: true,
        isApproved: true
      };

      // Используем админский API для создания отзыва
      const response = await api.post("/reviews/admin", reviewData);
      setReviews([...reviews, response.data]);
      setCreateDialogOpen(false);
      setError("");
      setCreateFormData({
        productId: "",
        email: "",
        rating: 0,
        text: "",
        createdAt: new Date().toISOString().slice(0, 16),
        isVerified: true,
        isApproved: true
      });
    } catch (error) {
      console.error("Error creating review:", error);
      setError("Ошибка при создании отзыва");
    }
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    const date = new Date(review.createdAt);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', review.createdAt);
      return;
    }
    const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    const formattedDate = localDate.toISOString().slice(0, 16);
    
    setEditFormData({
      email: review.email,
      rating: review.rating,
      text: review.text,
      isVerified: review.isVerified,
      isApproved: review.isApproved,
      createdAt: formattedDate
    });
    setEditDialogOpen(true);
  };

  const handleEditInputChange = (event) => {
    const { name, value, checked } = event.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: name === 'isVerified' || name === 'isApproved' ? checked : value,
    }));
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      console.log('Local date from form:', editFormData.createdAt);
      
      // Преобразуем строку даты в объект Date в локальной временной зоне
      const [datePart, timePart] = editFormData.createdAt.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      
      // Создаем дату в локальной временной зоне
      const localDate = new Date(year, month - 1, day, hours, minutes);
      
      // Преобразуем в UTC
      const utcDate = new Date(Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        localDate.getHours(),
        localDate.getMinutes()
      ));

      console.log('Converting date:', {
        localInput: editFormData.createdAt,
        utcDate: utcDate.toISOString()
      });

      const response = await api.put(`/reviews/${editingReview._id}`, {
        ...editFormData,
        createdAt: utcDate.toISOString()
      });

      setReviews(reviews.map((review) =>
        review._id === editingReview._id ? response.data : review
      ));
      
      setEditDialogOpen(false);
      setError("");
      setAlert({
        show: true,
        message: "Отзыв успешно обновлен",
        severity: "success"
      });
    } catch (error) {
      console.error("Error updating review:", error);
      setError(error.response?.data?.message || "Ошибка при обновлении отзыва");
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter((review) => {
    const matchesSearch =
      review.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.product?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      !selectedCategory ||
      review.product?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = async (id) => {
    if (window.confirm("Вы уверены, что хотите удалить этот отзыв?")) {
      try {
        await api.delete(`/reviews/${id}`);
        setReviews(reviews.filter((review) => review._id !== id));
        setError("");
      } catch (error) {
        console.error("Error deleting review:", error);
        setError("Ошибка при удалении отзыва");
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const ReviewCard = ({ review }) => (
    <Card sx={{ height: '100%', bgcolor: !review.isApproved ? 'action.hover' : 'inherit' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div" gutterBottom>
              {review.product?.name || "Unknown Product"}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {formatDate(review.createdAt)}
            </Typography>
          </Box>
          <Box>
            <IconButton 
              size="small" 
              onClick={() => handleEditClick(review)}
              sx={{ mr: 1 }}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDelete(review._id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Rating value={review.rating} readOnly precision={0.5} />
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {review.text}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip 
            label={review.isVerified ? "Verified" : "Pending"} 
            color={review.isVerified ? "success" : "warning"} 
            size="small" 
          />
          <Chip 
            label={review.isApproved ? "Approved" : "Awaiting Moderation"} 
            color={review.isApproved ? "success" : "warning"} 
            size="small" 
          />
          <Chip 
            label={review.email}
            color="primary"
            size="small"
          />
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2,
        mb: 3 
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' },
            fontWeight: 'bold'
          }}
        >
          Отзывы
        </Typography>
        <Box sx={{ 
          display: "flex", 
          gap: 2,
          width: isMobile ? '100%' : 'auto'
        }}>
          <TextField
            size="small"
            placeholder="Поиск отзывов..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ width: isMobile ? '100%' : 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Добавить отзыв
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredReviews.length > 0 ? (
            filteredReviews.map((review) => (
              <Grid item xs={12} sm={6} md={4} key={review._id}>
                <ReviewCard review={review} />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center">
                No reviews found
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Редактировать отзыв</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={editFormData.email}
              onChange={handleEditInputChange}
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Дата создания"
              name="createdAt"
              value={editFormData.createdAt}
              onChange={handleEditInputChange}
              InputLabelProps={{ shrink: true }}
            />
            <Box>
              <Typography component="legend">Рейтинг</Typography>
              <Rating
                name="rating"
                value={Number(editFormData.rating)}
                onChange={(event, newValue) => {
                  handleEditInputChange({
                    target: { name: 'rating', value: newValue }
                  });
                }}
                precision={0.5}
              />
            </Box>
            <TextField
              fullWidth
              label="Текст отзыва"
              name="text"
              value={editFormData.text}
              onChange={handleEditInputChange}
              multiline
              rows={4}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.isVerified}
                  onChange={handleEditInputChange}
                  name="isVerified"
                />
              }
              label="Проверенный отзыв"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.isApproved}
                  onChange={handleEditInputChange}
                  name="isApproved"
                />
              }
              label="Одобрен к публикации"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Сохранить"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Создать новый отзыв</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Категория</InputLabel>
              <Select
                name="categoryId"
                value={selectedCategory}
                onChange={handleCreateInputChange}
                label="Категория"
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedCategory}>
              <InputLabel>Продукт</InputLabel>
              <Select
                name="productId"
                value={createFormData.productId}
                onChange={handleCreateInputChange}
                label="Продукт"
              >
                {products
                  .filter(product => product.category._id === selectedCategory)
                  .map((product) => (
                    <MenuItem key={product._id} value={product._id}>
                      {product.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Email"
              name="email"
              value={createFormData.email}
              onChange={handleCreateInputChange}
            />

            <TextField
              fullWidth
              type="datetime-local"
              label="Дата и время создания"
              name="createdAt"
              value={createFormData.createdAt}
              onChange={handleCreateInputChange}
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography component="legend">Рейтинг</Typography>
              <Rating
                name="rating"
                value={Number(createFormData.rating)}
                onChange={(event, newValue) => {
                  handleCreateInputChange({
                    target: { name: 'rating', value: newValue }
                  });
                }}
                precision={0.5}
              />
            </Box>

            <TextField
              fullWidth
              label="Текст отзыва"
              name="text"
              value={createFormData.text}
              onChange={handleCreateInputChange}
              multiline
              rows={4}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Отмена</Button>
          <Button 
            onClick={handleCreateSubmit} 
            variant="contained"
            disabled={!createFormData.productId || !createFormData.email || !createFormData.text}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Reviews;
